import { useCallback, useEffect, useId } from 'react';
import { useTranslation, Trans } from '@aimsos/i18n';
import { AlertTriangle } from 'lucide-react';
import { Button, ModalLayout } from '@aimsos/ui';

/**
 * @typedef {{ id: string, fullName: string, email: string }} UserBulkTarget
 */

/**
 * Confirm bulk activate or deactivate for workspace tenant users.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {'activate'|'deactivate'} [props.variant='deactivate']
 * @param {UserBulkTarget[]} props.targets
 * @param {boolean} props.submitting
 * @param {() => void | Promise<void>} props.onConfirm
 */
const DeactivateUsersModal = ({
  isOpen,
  onClose,
  variant = 'deactivate',
  targets = [],
  submitting,
  onConfirm,
}) => {
  const { t } = useTranslation('workspaces');
  const titleId = useId();
  const count = targets.length;
  const single = count === 1 ? targets[0] : null;
  const isActivate = variant === 'activate';
  const ns = isActivate ? 'detail.users.activateModal' : 'detail.users.deactivateModal';

  const handleDismiss = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [onClose, submitting]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleDismiss();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, handleDismiss]);

  const title =
    count === 1 ? t(`${ns}.titleSingle`) : t(`${ns}.titleMany`);

  const closeAria = t(`${ns}.closeAria`);

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={handleDismiss}
      title={title}
      titleId={titleId}
      closeAriaLabel={closeAria}
      maxWidthClassName="max-w-lg"
    >
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        <div className="mx-auto flex max-w-md flex-col items-center gap-5 text-center">
          <div
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400"
            aria-hidden
          >
            <AlertTriangle className="h-8 w-8" strokeWidth={2} />
          </div>

          {single ? (
            <p className="max-w-md text-sm leading-relaxed text-content">
              <Trans
                ns="workspaces"
                i18nKey={`${ns}.messageSingle`}
                values={{ name: single.fullName, email: single.email }}
                components={{
                  highlight: <span className="font-semibold text-content" />,
                }}
              />
            </p>
          ) : (
            <p className="max-w-md text-sm leading-relaxed text-content">
              {t(`${ns}.messageMany`, { count })}
            </p>
          )}

          {!isActivate && (
            <p className="max-w-md text-sm leading-relaxed text-content-muted">
              {t('detail.users.deactivateModal.submessage')}
            </p>
          )}
        </div>
      </div>

      <div className="shrink-0 bg-surface-muted/20 px-5 py-4">
        <div className="flex justify-center gap-4">
          <Button variant="secondary" type="button" size="md" onClick={handleDismiss} disabled={submitting}>
            {t(`${ns}.cancel`)}
          </Button>
          <Button
            variant={isActivate ? 'primary' : 'danger'}
            type="button"
            size="md"
            loading={submitting}
            onClick={onConfirm}
          >
            {count === 1 ? t(`${ns}.confirmSingle`) : t(`${ns}.confirmMany`)}
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
};

export default DeactivateUsersModal;
